import {Types}              from "@Common/types";
import {cnv}                from "@Common/utils/arr-path";
import {Fetcher}            from "@Common/utils/browser/fetcher";
import {buildUrlQuery}      from "@Common/utils/build-url-query";
import {reverse, sortMulti} from "@Common/utils/collections";
import {chain}              from "@Common/utils/fs";
import {omit}               from "@Common/utils/objects";
import {upperCase1}         from "@Common/utils/strings";

import {AGENT_LEVEL, BRANCH_LEVEL, COMPANY_LEVEL} from "@Xpresso/routes/common/logic/user-level";

interface Fetcher {
    getJson  : <T,>(path:string, returnJson?:boolean)            => Promise<T>,
    postJson : <T,>(path:string, data?:any, returnJson?:boolean) => Promise<T>,
}

export const createListingsApis = ({domain, fetcher}:{domain:string, fetcher:Fetcher}) => ({
    createListing: (payload:Types.Apis.Payloads.Listings.Listings.CreateListing.Request): Promise<Types.Apis.Payloads.Listings.Listings.CreateListing.Response> => (
        fetcher.postJson<Response>("/listings/xd/create", payload, false).then((res) => (
            res.status < 400 ? res.text() : Promise.reject(res)
        ))
    ),

    deleteListing: ({listingId}:Types.Apis.Payloads.Listings.Listings.DeleteListing.Params): Promise<Types.Apis.Payloads.Listings.Listings.DeleteListing.Response> => (
        fetch(`${domain}/listings/${listingId}`, {method: "DELETE"}).then((res) => (
            res.status < 400 ? Promise.resolve() : Promise.reject(res)
        ))
    ),

    getEvent: async (id) => {
        const json = await fetcher.getJson(`/events?id=${id}`);
        return json['records'][0];
    },

    getListing: async (req:Types.Apis.Payloads.Listings.GetListing.Request): Promise<Types.Apis.Payloads.Listings.GetListing.Response> => {
        const json = await fetcher.getJson<Types.Apis.Payloads.Listings.GetListings.Response>("/listings" + buildUrlQuery(req));
        return json.records?.[0] || null;
    },

    getCleanUserPrefs: async () => {
        return await fetcher.getJson(`/user-prefs-products?programType=apm`);
    },
    saveCleanUserPrefs: async (prefs) => {
        return await fetcher.postJson(`/user-prefs?programType=apm&clean=true`, {
            ...prefs,
            setByAgent: true,
        });
    },
    getProgramPrefs: async () => {
        // await waitTimeout(500);
        return await fetcher.getJson(`/program-prefs?programType=apm`);
    },

    getListingsLike: (params:Types.Apis.Payloads.Listings.Listings.Like.Params): Promise<Types.Apis.Payloads.Listings.Listings.Like.Response> => (
        fetcher.getJson("/listings/like" + buildUrlQuery(params))
    ),

    getMyListings: async ({filter, sorting, limit, allListingsSearch, emulation, skip}) => {

        return await fetcher.getJson("/listings/like" + buildUrlQuery({
            ...filter && {
                omniSearchKeys: "streetAddress,city,mlsId,zip",
                omniSearchValue: filter,
            },
            sort: sorting ? (
                sorting.value + " " + upperCase1(sorting.direction)
            ) : (
                `if(dateSold,dateSold,dateListed) Desc`
            ),
            ...(allListingsSearch && filter) ? {
                allCompanyRecords: true,
            } : {
                idAgent : emulation?.agentId,
                idBranch: emulation?.branchId,
            },
            limit, skip,
        }));
    },

    getRule: async (ruleId) => {
        return await fetcher.getJson(`/cc-rule-creations?where={"_id":"${ruleId}"}`);
    },

    setEvent: async (event) => {
        return fetcher.postJson(`/updateEvent/${event._id}`, event);
    },

    updateListing: (payload:Types.Apis.Payloads.Listings.Listings.UpdateListing.Request): Promise<Types.Apis.Payloads.Listings.Listings.UpdateListing.Response> => (
        fetcher.postJson<Response>(`/listings/xd/update`, payload, false).then((res) => res.status < 400 ? Promise.resolve() : Promise.reject(res))
    ),


    enrollment: {
        getUserEnrollments: async ({agentId, programId}) => {
            // await waitTimeout(600);
            // return [];
            return chain(
                await fetcher.getJson<any>("/scheduled-campaigns/user-prog-prefs" + buildUrlQuery({
                    idAgent: agentId,
                    id_program: programId,
                    limit: 9999,
                })),
                (_) => _?.records,
            );
            // return (Array.isArray(res) && res.length === 0) ? null : res;

        },
        saveUserEnrollment: async (enrollment, {newEnrollment=false, unenrollment=null}={}) => {
            // console.log("API: saveUserEnrollment", enrollment);
            // await waitTimeout(600);
            const savingData = chain(
                {
                    ...enrollment,

                    enrolled: enrollment.enrolled ?? false,
                    distribution_options: enrollment.distribution_options ?? {},
                    payment_options: enrollment.payment_options ?? {},

                    ...newEnrollment && {
                        enrolled: true,
                        unenroll_reason: null,
                        unenroll_time: null,
                    },
                },
                (_) => cnv(_, [], (_) => !unenrollment ? _ : ({
                    ...omit(_, ["enrolledTime"]),

                    enrolled: false,
                    unenroll_reason: unenrollment.reason,
                    unenroll_time: new Date().toISOString(),
                })),
                (_) => cnv(_, ["unenroll_reason"], (_) => _ ?? ""),
                (_) => cnv(_, ["unenroll_time"], (_) => _ ?? ""),
            );

            const result = await fetcher.postJson<any>(
                "/scheduled-campaigns/user-prog-prefs" + (enrollment._id ? "/" + enrollment._id : "") +
                buildUrlQuery({
                    // idAgent: agentId,
                    ...newEnrollment && {
                        newEnrollment: true,
                    },
                    ...unenrollment && {
                        unenrollment: true,
                    },
                }),
                savingData,
            );

            if (result.error) {
                throw result;
            }
            const {_id} = chain(
                result,
                // (_) => _?.records?.[0],
            );
            return {
                ...savingData,
                ..._id&&{_id},
            };
        },
        getProgram: async ({userLevel, branchId}) => {
            // await waitTimeout(300);
            return chain(
                await fetcher.getJson<any>("/scheduled-campaigns/programs?limit=999"),
                (_) => _?.records,
                (_) => _?.filter((p) => (
                    p.program.active &&
                    (
                        userLevel === COMPANY_LEVEL && p.program.useCompany ||
                        userLevel === BRANCH_LEVEL  && p.program.useBranch  ||
                        userLevel === AGENT_LEVEL   && p.program.useAgent   ||
                        false
                    ) &&
                    (
                        !p.program.idBranch ||
                        // !branchId ||
                        branchId == p.program.idBranch
                    )
                )),
                (_) => chain(_,
                    (_) => sortMulti(_, [
                        (p) => p.program.idBranch ? 1 : 0,
                        (p) => p.program.dateModified,
                    ]),
                    reverse,
                ),
                (_) => _?.[0]?.program,
                // (_) => {
                //     const editions = _.programPreferences.editions.map((e) => {
                //         return {
                //             ...e,
                //             series: _.programPreferences.series.find((s) => s.id === e.seriesId)?.name || "error"
                //         };
                //     });
                //     _.programPreferences.editions = editions;
                //     return _;
                // },
            );
        },
        getHistoricRuns: async ({agentId}) => {
            return chain(
                await fetcher.getJson<any>("/scheduled-campaigns/runs" + buildUrlQuery({
                    // date_to: parseDateFormat("yyyy-mm-dd").formatDate(tomorrow()),
                    idAgent: agentId,
                })),
                (_) => _?.records,
            );
        },
    },
});
